<template>
    <div class="card" style="box-shadow: none">
        <form class="card-body me-3 ms-3" @submit.prevent="handleSubmit">
            <div v-for="(inputGroup, index) in filteredFormConfig" :key="index">
                <!-- 單一物件的情況 -->
                <div v-if="!Array.isArray(inputGroup)" class="form-group-row">
                    <UserFormGroupInput
                        ref="inputRefs"
                        v-bind="inputGroup"
                        :key="
                            inputGroup.field === 'stations'
                                ? `stations-${componentKey}`
                                : inputGroup.field === 'managed_stations'
                                ? `managed-stations-${componentKey}`
                                : inputGroup.field
                        "
                        :add-option-params="getAddOptionParams(inputGroup)"
                        v-model="formData[inputGroup.field]"
                        @valid="handleValidation"
                    />
                </div>
                <!-- 多個物件（數組）的情況 -->
                <div v-else class="form-group-row d-flex">
                    <div
                        v-for="(inputConfig, subIndex) in inputGroup"
                        :key="subIndex"
                        class="flex-fill"
                    >
                        <UserFormGroupInput
                            ref="inputRefs"
                            v-bind="inputConfig"
                            :key="
                                inputConfig.field === 'stations'
                                    ? `stations-${componentKey}`
                                    : inputConfig.field === 'managed_stations'
                                    ? `managed-stations-${componentKey}`
                                    : inputConfig.field
                            "
                            :add-option-params="getAddOptionParams(inputConfig)"
                            v-model="formData[inputConfig.field]"
                            @valid="handleValidation"
                        />
                    </div>
                </div>
            </div>
            <div class="d-grid gap-2 d-md-flex justify-content-md-start">
                <button
                    type="submit"
                    class="btn btn-primary"
                    :disabled="submitDisabled"
                >
                    {{ $t('users.users_msg__user_form_submit') }}
                </button>
                <button
                    type="button"
                    class="btn btn-secondary"
                    @click="handleReset"
                >
                    {{ $t('users.users_msg__create_account_reset') }}
                </button>
            </div>
        </form>
    </div>
</template>

<script>
import { ref, onMounted, inject, watch, computed } from 'vue';
import { UserFormGroupInput } from '@/components';
import { useI18n } from 'vue-i18n';
import { fetchEditUserConfig, editUser, getUser } from '@/api/users';
import {
    transformFormPayload,
    transformValueForForm,
} from '@/utils/formPayload';

export default {
    name: 'UserEdit',
    components: {
        UserFormGroupInput,
    },
    props: {
        user: {
            type: Array,
            required: true,
        },
    },
    setup(props, { emit }) {
        const formConfig = ref([]);
        const formData = ref({});
        const submitDisabled = ref(false);
        const componentKey = ref(0); // 用於強制重新渲染
        const { t } = useI18n();
        const Swal = inject('$swal');

        const user = ref({});
        const inputRefs = ref([]);
        const isInitializing = ref(true); // 新增初始化標記

        const accountLevel = ref('Account');
        const stationFieldName = ref('stations');
        const managedStationsFieldName = ref('managed_stations');

        const activeAccountLevel = computed(
            () => formData.value.account_level || accountLevel.value
        );

        const filteredFormConfig = computed(() => {
            const allow = cfg =>
                !cfg.visible_when ||
                cfg.visible_when.includes(activeAccountLevel.value);

            return formConfig.value
                .map(item => (Array.isArray(item) ? item.filter(allow) : item))
                .filter(item =>
                    Array.isArray(item) ? item.length : allow(item)
                );
        });

        const initialize = async () => {
            isInitializing.value = true; // 開始初始化
            await loadUserDetail();
            await loadFormConfig();
            initializeFormData();
            // 在初始化表單數據後，確保 stations 選項正確載入
            await ensureStationsLoaded();
            isInitializing.value = false; // 初始化完成
        };

        onMounted(async () => {
            await initialize();
        });

        const loadFormConfig = async () => {
            try {
                formConfig.value = await fetchEditUserConfig(
                    accountLevel.value
                );

                const initializeStationsField = fieldName => {
                    const stationField = formConfig.value.find(
                        config => config && config.field === fieldName
                    );
                    if (stationField) {
                        stationField.options_url =
                            '/users/stations?search_fields=club__id&search=0&search_mode=exact';
                    }
                };

                initializeStationsField(stationFieldName.value);
                initializeStationsField(managedStationsFieldName.value);
            } catch (error) {
                console.error('Error fetching form configuration:', error);
            }
        };

        const ensureStationsLoaded = async () => {
            const loadStationsField = async fieldName => {
                const stationField = formConfig.value.find(
                    config => config && config.field === fieldName
                );
                if (!stationField) return;

                if (user.value.club && user.value.club !== 0) {
                    const newUrl = `/users/stations?search_fields=club__id&search=${user.value.club}&search_mode=exact`;
                    stationField.options_url = newUrl;

                    await new Promise(resolve => setTimeout(resolve, 500));

                    const userFieldValue = user.value[fieldName];
                    if (
                        Array.isArray(userFieldValue) &&
                        userFieldValue.length > 0
                    ) {
                        formData.value[fieldName] = [...userFieldValue];
                    }
                } else {
                    console.warn(
                        'User has no club, keeping empty stations URL'
                    );
                }
            };

            await loadStationsField(stationFieldName.value);
            await loadStationsField(managedStationsFieldName.value);

            componentKey.value++;
        };

        const loadUserDetail = async () => {
            try {
                user.value = await getUser(props.user[0]);
                accountLevel.value = user.value.groups[0];
            } catch (error) {
                console.error('Error fetching user detail:', error);
                if (error.response && error.response.status === 404) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: t('users.users_msg__user_not_found'),
                    }).then(() => {
                        // Close the modal
                        emit('resetModal');
                    });
                }
            }
        };

        const initializeFormData = () => {
            formConfig.value.forEach(config => {
                if (Array.isArray(config)) {
                    config.forEach(subConfig => {
                        initializeFieldData(subConfig);
                    });
                } else {
                    initializeFieldData(config);
                }
            });
        };

        // 根據 config 的 payload_type 初始化單個欄位
        const initializeFieldData = config => {
            if (!config || !config.field) return;

            const field = config.field;
            const apiValue = user.value[field];

            // 使用 transformValueForForm 根據 payload_type 轉換值
            const formValue = transformValueForForm(apiValue, config);

            // 處理特殊情況
            if (formValue === undefined || formValue === null) {
                // 根據類型設定預設值
                if (config.type === 'multi-select') {
                    formData.value[field] = [];
                    config.value = [];
                } else {
                    formData.value[field] = '';
                    config.value = '';
                }
            } else {
                formData.value[field] = Array.isArray(formValue)
                    ? [...formValue]
                    : formValue;
                config.value = Array.isArray(formValue)
                    ? [...formValue]
                    : formValue;
            }
        };

        // 監控 user prop 的變化，更新表單數據
        watch(() => props.user, initializeFormData);

        const updateStationsOptionsByClubId = clubId => {
            if (isInitializing.value) {
                return;
            }

            const updateField = fieldName => {
                const stationField = formConfig.value.find(
                    config => config && config.field === fieldName
                );
                if (
                    stationField &&
                    (stationField.type === 'select' ||
                        stationField.type === 'multi-select')
                ) {
                    formData.value[fieldName] = [];
                    stationField.value = [];

                    if (clubId && clubId !== 0) {
                        const newUrl = `/users/stations?search_fields=club__id&search=${clubId}&search_mode=exact`;
                        stationField.options_url = newUrl;
                    } else {
                        stationField.options_url =
                            '/users/stations?search_fields=club__id&search=0&search_mode=exact';
                    }
                }
            };

            updateField(stationFieldName.value);
            updateField(managedStationsFieldName.value);
            componentKey.value++;
        };

        // 當 club 變更時，透過修改 options_url 來觸發 stations 的重新載入
        watch(() => formData.value.club, updateStationsOptionsByClubId);

        const updateStationsOptionsByClubName = clubName => {
            if (isInitializing.value) {
                return;
            }

            const updateField = fieldName => {
                const stationField = formConfig.value.find(
                    config => config && config.field === fieldName
                );
                if (
                    stationField &&
                    (stationField.type === 'select' ||
                        stationField.type === 'multi-select')
                ) {
                    formData.value[fieldName] = [];
                    stationField.value = [];

                    if (clubName && clubName.trim() !== '') {
                        const newUrl = `/users/stations?search_fields=club__name&search=${encodeURIComponent(
                            clubName
                        )}`;
                        stationField.options_url = newUrl;
                    } else {
                        stationField.options_url =
                            '/users/stations?search_fields=club__id&search=0&search_mode=exact';
                    }
                }
            };

            updateField(stationFieldName.value);
            updateField(managedStationsFieldName.value);
            componentKey.value++;
        };

        // 當 club_name 變更時，透過修改 options_url 來觸發 stations 的重新載入
        watch(() => formData.value.club_name, updateStationsOptionsByClubName);

        const handleValidation = isValid => {
            submitDisabled.value = !isValid;
        };

        const handleSubmit = async () => {
            inputRefs.value.forEach(ref => {
                ref.checkValue();
            });
            if (submitDisabled.value) {
                return;
            }
            Swal.fire({
                title: t('users.users_msg__editing_user'),
                text: t('users.users_msg__please_wait'),
                showConfirmButton: false,
                willOpen: () => {
                    Swal.showLoading();
                },
            });

            // 編輯模式送出時排除 readonly 欄位，避免 patch payload 帶入唯讀資料
            const editableFormConfig = filteredFormConfig.value
                .map(config =>
                    Array.isArray(config)
                        ? config.filter(item => item?.type !== 'readonly')
                        : config?.type !== 'readonly'
                        ? config
                        : null
                )
                .filter(config =>
                    Array.isArray(config) ? config.length > 0 : !!config
                );

            // 使用 transformFormPayload 處理 payload 格式轉換（編輯模式：跳過未變更的欄位）
            const userData = transformFormPayload(
                formData.value,
                editableFormConfig,
                {
                    originalData: user.value,
                    skipUnchanged: true,
                }
            );

            Swal.close();

            if (Object.keys(userData).length === 0) {
                Swal.fire({
                    icon: 'info',
                    title: t('users.users_msg__no_changes_have_been_made'),
                });
                return;
            }

            try {
                await editUser(props.user[0], userData);
                await Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: t('users.users_msg__edit_a_new_user_successful', {
                        username: userData.username,
                    }),
                });
                emit('submitSuccess');
                Swal.close();
            } catch (error) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: t(`users.${error.response.data.detail}`),
                });
            }
        };

        const handleReset = async () => {
            isInitializing.value = true; // 重置時也設為初始化狀態
            await initialize();
        };

        const getAddOptionParams = inputConfig => {
            // 如果是 stations / managed_stations 欄位，加入 club 參數
            if (
                inputConfig.field === 'stations' ||
                inputConfig.field === 'managed_stations'
            ) {
                // 獲取 club ID，優先使用 club，如果沒有則嘗試從 userSelf 獲取
                let clubId = formData.value.club;

                // 如果 formData.value.club 沒有值，但有 club_name（readonly 模式）
                if (!clubId && formData.value.club_name) {
                    clubId = user.value?.club;
                }

                if (!clubId) {
                    console.warn('No club ID available for adding station');
                    // 可以選擇顯示警告或返回空對象
                    // Swal.fire({
                    //     icon: 'warning',
                    //     title: 'Warning',
                    //     text: t('users.users_msg__please_select_club_first'),
                    // });
                    return {};
                }

                return {
                    club: clubId,
                };
            }
            // 其他欄位可以根據需要添加不同的參數
            return {};
        };

        return {
            formConfig,
            filteredFormConfig,
            formData,
            submitDisabled,
            componentKey,
            handleSubmit,
            handleValidation,
            handleReset,
            inputRefs,
            getAddOptionParams,
        };
    },
};
</script>

<style scoped>
.form-group-row {
    margin-bottom: 1rem;
    text-align: left;
}

.d-flex {
    display: flex;
    gap: 1rem;
}

.flex-fill {
    flex: 1;
}
</style>
